'use client';

import { useEffect, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@lib/hooks';
import { hydrateCart } from '@lib/features/cart/cartSlice';

const CART_STORAGE_KEY = 'uxd-cart-items';
const LEGACY_PERSIST_KEY = 'persist:root';

function parseJson(value) {
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

function normalizeCartItems(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function readLegacyCartItems() {
    const legacyRoot = parseJson(window.localStorage.getItem(LEGACY_PERSIST_KEY));
    const legacyCart = typeof legacyRoot?.cart === 'string' ? parseJson(legacyRoot.cart) : null;

    return normalizeCartItems(legacyCart?.items);
}

function readStoredCartItems() {
    return normalizeCartItems(parseJson(window.localStorage.getItem(CART_STORAGE_KEY)));
}

function readInitialCartItems() {
    const storedItems = parseJson(window.localStorage.getItem(CART_STORAGE_KEY));

    if (storedItems && typeof storedItems === 'object' && !Array.isArray(storedItems)) {
        return storedItems;
    }

    return readLegacyCartItems();
}

function serializeCartItems(items) {
    return JSON.stringify(normalizeCartItems(items));
}

function mergeCartItems(storedItems, previousItems, currentItems, mutation) {
    if (mutation?.type === 'clear') {
        return {};
    }

    const mergedItems = { ...storedItems };

    if (mutation?.type === 'remove' && mutation.altId) {
        delete mergedItems[mutation.altId];
    } else {
        for (const altId of Object.keys(previousItems)) {
            if (!Object.prototype.hasOwnProperty.call(currentItems, altId)) {
                delete mergedItems[altId];
            }
        }
    }

    for (const [altId, item] of Object.entries(currentItems)) {
        mergedItems[altId] = item;
    }

    return mergedItems;
}

export default function CartPersistence() {
    const dispatch = useAppDispatch();
    const { items, lastMutation } = useAppSelector((state) => state.cart);
    const hasHydrated = useRef(false);
    const lastPersistedItems = useRef({});
    const currentItemsRef = useRef(items);
    const currentMutationRef = useRef(lastMutation);

    useEffect(() => {
        currentItemsRef.current = items;
        currentMutationRef.current = lastMutation;
    }, [items, lastMutation]);

    useEffect(() => {
        const nextItems = readInitialCartItems();
        lastPersistedItems.current = nextItems;

        if (Object.keys(nextItems).length) {
            dispatch(hydrateCart(nextItems));
            window.localStorage.setItem(CART_STORAGE_KEY, serializeCartItems(nextItems));
        }

        hasHydrated.current = true;

        const handleStorage = (event) => {
            if (event.storageArea !== window.localStorage || event.key !== CART_STORAGE_KEY) {
                return;
            }

            const incomingItems = normalizeCartItems(parseJson(event.newValue));
            const latestItems = normalizeCartItems(currentItemsRef.current);
            const hasPendingLocalChange =
                serializeCartItems(latestItems) !== serializeCartItems(lastPersistedItems.current);
            const nextItems = hasPendingLocalChange
                ? mergeCartItems(
                    incomingItems,
                    lastPersistedItems.current,
                    latestItems,
                    currentMutationRef.current
                )
                : incomingItems;
            const nextJson = serializeCartItems(nextItems);

            if (hasPendingLocalChange && window.localStorage.getItem(CART_STORAGE_KEY) !== nextJson) {
                window.localStorage.setItem(CART_STORAGE_KEY, nextJson);
            }

            lastPersistedItems.current = nextItems;
            dispatch(hydrateCart(nextItems));
        };

        window.addEventListener('storage', handleStorage);

        return () => {
            window.removeEventListener('storage', handleStorage);
        };
    }, [dispatch]);

    useEffect(() => {
        if (!hasHydrated.current) {
            return;
        }

        const currentItems = normalizeCartItems(items);

        if (lastMutation?.type === 'hydrate' || lastMutation?.type === 'init') {
            lastPersistedItems.current = currentItems;
            return;
        }

        const mergedItems = mergeCartItems(
            readStoredCartItems(),
            lastPersistedItems.current,
            currentItems,
            lastMutation
        );
        const mergedJson = serializeCartItems(mergedItems);

        if (window.localStorage.getItem(CART_STORAGE_KEY) !== mergedJson) {
            window.localStorage.setItem(CART_STORAGE_KEY, mergedJson);
        }

        lastPersistedItems.current = mergedItems;

        if (serializeCartItems(currentItems) !== mergedJson) {
            dispatch(hydrateCart(mergedItems));
        }
    }, [dispatch, items, lastMutation]);

    return null;
}
